import { toNDigits } from '@mail/cross-sizes-utils';
import { Button } from '@vkontakte/vkui';
import { Rubles } from 'Cloud/Application/Rubles';
import clsx from 'clsx';
import sha1 from 'js-sha1';
import { pathOr } from 'ramda';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { downloadItem } from 'reactApp/appHelpers/appDownload';
import {
    HIDE_ADS,
    IS_MOBILE_BROWSER,
    IS_MY_TEAM,
    IS_ONPREMISE,
    IS_PUBLIC_FOLDER,
    IS_PUBLIC_OF_OVERQUOTA,
    IS_STOCK,
    PUBLIC_SHOVE,
} from 'reactApp/appHelpers/configHelpers';
import { mailSpaceTrialPromo } from 'reactApp/appHelpers/featuresHelpers';
import { logInAccountModalFeature } from 'reactApp/appHelpers/featuresHelpers/features/logInAccountModal';
import { IS_GO_TO_PHONE_PUBLIC_EDITOR_STEP } from 'reactApp/appHelpers/featuresHelpers/features/phonePublicEditor';
import { publicAppDownloadBannerTouch } from 'reactApp/appHelpers/featuresHelpers/features/publicAppDownloadBannerTouch';
import { publicBenefitsOfAuthorizationPromo } from 'reactApp/appHelpers/featuresHelpers/features/publicBenefitsOfAuthorization';
import { showPublicFileDownloadButton } from 'reactApp/appHelpers/featuresHelpers/features/publicFileDownloadButton';
import { showPublicFileOpenButton } from 'reactApp/appHelpers/featuresHelpers/features/publicFileOpenButton';
import { abPublicMobileHeaderValue } from 'reactApp/appHelpers/featuresHelpers/features/publicHeader';
import { overquotaUserLockInPublic, publicOverquota } from 'reactApp/appHelpers/featuresHelpers/features/publicOverquota';
import { abPublicMobileSnackbarToggle } from 'reactApp/appHelpers/featuresHelpers/features/publicSnackbar';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { getWatchInCloudButton } from 'reactApp/appHelpers/featuresHelpers/features/watchInCloudButton';
import { isViewContentOnly } from 'reactApp/appHelpers/settingsHelpers';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { useIntersectionObserver } from 'reactApp/hooks/useInterSectionObserver';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { isOnPremR7Product } from 'reactApp/modules/features/features.helpers';
import { getWeblinkFromPublicId, isDocument, isPreviewable } from 'reactApp/modules/file/utils';
import { authPopup } from 'reactApp/modules/ph/ph.thunkActions';
import { isDownloadActionAvailable } from 'reactApp/modules/public/public.selectors';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { hideSnackbarAction, showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import type { StockFolder } from 'reactApp/modules/stock/stock.type';
import { getCurrentItem, getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { type CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { getOverQuota, UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { selectIsItemEditable } from 'reactApp/modules/viewer/selectIsViewerItemEditable';
import { removeViewerPlaceholder } from 'reactApp/modules/viewer/viewer.helpers';
import { useRouteChangeProcessing } from 'reactApp/sections/MainPage/hooks/useRouteChangeProcessing';
import { usePublicSnackbar } from 'reactApp/sections/MobilePublicPage/usePublicSnackbar';
import type { RootState } from 'reactApp/store';
import { EmptyPublicOverquota } from 'reactApp/ui/Empty/new/EmptyPublicOverquota';
import { MailspacePublicTrialBanner } from 'reactApp/ui/MailspacePublicTrialBanner/MailspacePublicTrialBanner';
import { MobileViewerItem } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerItem/MobileViewerItem';
import { useCallbacks } from 'reactApp/ui/Mobile/OptionsModal/hooks/useCallbacks';
import { OptionsModalSource } from 'reactApp/ui/Mobile/OptionsModal/OptionsModal.constants';
import { renderMobileOptionsModal } from 'reactApp/ui/Mobile/OptionsModal/OptionsModal.helpers';
import { ViewerToolbarMobileNew } from 'reactApp/ui/Mobile/ViewerToolbarMobileNew/ViewerToolbarMobileNew';
import { MobileAppBanner } from 'reactApp/ui/MobileAppBanner/MobileAppBanner';
import { Overquota } from 'reactApp/ui/Public/Components/Overquota/Overquota';
import { sendViewerDwh } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { RollContentAds } from 'reactApp/ui/RollContent/RollContentAds';
import { handleOnMobileApp } from 'reactApp/ui/ViewerToolbar/ViewerToolbar.helpers';
import type { IFile } from 'reactApp/ui/ViewerToolbar/ViewerToolbar.types';
import { DownloadIcon } from 'reactApp/ui/VKUIIcons';
import { sendDwh as sendPublicDwh, sendXray } from 'reactApp/utils/ga';
import { sendCurrTimeRadar, sendPerfMetrics } from 'reactApp/utils/helpers';
import { ECategoryGa } from 'reactApp/utils/paymentGa';
import { EQueryParams } from 'server/helpers/getRequestParams';

import styles from './PublicFile.css';

// 'public-file' - общая категория событий для белого и черного одиночного паблика
const EVENT_CATEGORY = 'public-file';

// высота тулбара
const TOOLBAR_HEIGHT = 66;

// высота шапки
const HEADER_HEIGHT = 50;

// высота баннера рекламы
const DEFAULT_TOP_HEIGHT = 114;

const SNACKBAR_Z_INDEX = 1200;

const DOWNLOAD_APP_SNAKCBAR_ID = 'download-app';

const openEditorInNewTab = () => {
    const url = new URL(location.href);
    url.searchParams.append('tpss', '1');
    window.open(url.href, '_blank', 'noopener noreferrer');
};

// eslint-disable-next-line max-lines-per-function, complexity
export const PublicFileConnected = memo(({ storage }: { storage: EStorageType }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [snackbarVisible, setSnackbarVisible] = useState(false);
    const [bannerRoot, setBannerRoot] = useState<Element | null>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();
    const isAuthorized = !useSelector(UserSelectors.isAnonymous);
    const isBiz = useSelector(UserSelectors.isBizUser);
    const isWebview = useSelector(EnvironmentSelectors.isWebview);
    const fileOrFolder = useSelector(getCurrentItem) as CloudFile;
    const isOverquota = useSelector(UserQuotaSelectors.isOverquotaBySpaceAndApi);
    const quota = useSelector(getOverQuota);

    let routeId = useSelector(getCurrentRouteId);
    if (!IS_PUBLIC_FOLDER && storage === EStorageType.stock) {
        routeId = (fileOrFolder as unknown as StockFolder)?.childs[0];
    }

    const file = useSelector((state: RootState) => getStorageItemById(state, storage, routeId)) as CloudFile;

    const params = useSelector(SettingsSelectors.getQueryParams);
    const fromDeeplink = params[EQueryParams.fromDeeplink];
    const viewerOnly = params.viewerOnly;

    const isSuperAppWebView = isWebview && fromDeeplink;
    const showDisabledForOverquotaOwner = IS_PUBLIC_OF_OVERQUOTA && publicOverquota;
    const withMailSpacePromo = mailSpaceTrialPromo && mailSpaceTrialPromo !== 'a';
    const isPublic = storage === EStorageType.public;

    const item: IFile = {
        id: file?.id,
        name: file?.isFolder ? file?.name : file?.nameWithoutExt ?? '',
        size: toNDigits({ bytes: file?.size, maxDigitsCount: 3 })?.value,
        extension: file?.ext,
        date: pathOr(0, ['mtime'], file),
        expires: pathOr(0, ['weblinkExpires'], file),
        kind: file?.kind,
        subKind: pathOr('', ['subKind'], file),
        weblinkSeoEnabled: seoEnable && isPublic && file && 'weblinkSeoEnabled' in file && file.weblinkSeoEnabled,
    };
    const { showEditButton } = useSelector((state: RootState) =>
        selectIsItemEditable(state, {
            file,
            itemStorage: storage,
            allowPublicEdit: IS_GO_TO_PHONE_PUBLIC_EDITOR_STEP,
        })
    );

    const isOpenAccess = file && 'weblinkAccessRights' in file && !!file.weblinkAccessRights;
    const showOpenButton = showEditButton && showPublicFileOpenButton && isOpenAccess;
    // чекаем флаг на веблинке на онпремисе. Не чекаем, что это пустая папка, тк мы в компоненте паблик файла
    // для итемов с превью кнопку НЕ показываем, чтобы она не налазила на превью
    const isDownloadable = useSelector(isDownloadActionAvailable);
    const isPreviewableItem = file && isPreviewable(file, storage);
    const showDownloadButton = isDownloadable && showPublicFileDownloadButton && (!isPreviewableItem || showEditButton);
    const showBottomControls = showOpenButton || showDownloadButton;
    const isDocumentItem = isDocument(item);

    const isWatchInCloudButton = getWatchInCloudButton().includes(item.extension) && !isWebview && !viewerOnly;
    const baseShowSnackbar =
        !isWebview &&
        !viewerOnly &&
        !isWatchInCloudButton &&
        !IS_ONPREMISE &&
        !IS_MY_TEAM &&
        !(IS_STOCK && isPreviewableItem && isDocumentItem) &&
        (!logInAccountModalFeature || isAuthorized);

    /**
     * @expFlag public-app-download-banner
     * @ticket CLOUDWEB-20131
     * @ifLose Оствить baseShowSnackbar и showSnackbar
     * @start
     */
    const showSnacbarExp =
        baseShowSnackbar &&
        publicAppDownloadBannerTouch &&
        (publicAppDownloadBannerTouch.type === 'b' || publicAppDownloadBannerTouch.type === 'c');
    const showSnackbar = baseShowSnackbar && !showSnacbarExp && !publicBenefitsOfAuthorizationPromo;

    usePublicSnackbar(showSnacbarExp, false, SNACKBAR_Z_INDEX);

    /**
     * @expFlag public-app-download-banner
     * @ticket CLOUDWEB-20131
     * @ifLose Оствить baseShowSnackbar и showSnackbar
     * @end
     */

    useEffect(() => {
        if (!showSnackbar && snackbarVisible) {
            dispatch(hideSnackbarAction(DOWNLOAD_APP_SNAKCBAR_ID));
        }
    }, [dispatch, showSnackbar, snackbarVisible]);

    const sendDwh = useCallback(
        (data) => {
            sendViewerDwh({
                id_media: sha1(file?.id || ''),
                source: `touch-${storage}`,
                is_stories: false,
                id_public: getWeblinkFromPublicId(file?.id) ?? 'None',
                ...data,
            });
        },
        [file, storage]
    );

    useEffect(() => {
        sendPerfMetrics(`mpubfile`);
        sendCurrTimeRadar(`mpubfile_mnt`);

        Rubles.runPhoneTopBannerUpdater();

        if (!isBiz && !isWebview) {
            Rubles.updateMobSplash();
        }
    }, []);

    useEffect(() => {
        const root = document.querySelector('#mobile-app-banner');
        setBannerRoot(root);
    }, []);

    const handleOnMobileAppClick = useCallback(() => {
        sendPublicDwh({
            eventCategory: ECategoryGa.public,
            action: 'promo-app_click',
            dwhData: {
                type_promo: isWatchInCloudButton ? 'blue-button' : 'snack',
                extension: file?.ext,
            },
        });
        handleOnMobileApp();
    }, [handleOnMobileApp, isWatchInCloudButton, file]);

    useEffect(() => {
        if (isWatchInCloudButton || (!isWebview && !viewerOnly && file?.ext)) {
            sendPublicDwh({
                eventCategory: ECategoryGa.public,
                action: 'promo-app_show',
                dwhData: {
                    type_promo: isWatchInCloudButton ? 'blue-button' : 'snack',
                    extension: file.ext,
                },
            });
        }
    }, [file]);

    const toggleSnackbar = (e?: MouseEvent) => {
        const hideSnackbar = hideSnackbarAction(DOWNLOAD_APP_SNAKCBAR_ID);

        // Если не кликнули по просмотрщику => скрываем снэкбар
        if (e?.target && !previewRef.current?.contains(e?.target as Node)) {
            dispatch(hideSnackbar);
            setSnackbarVisible(false);
            return;
        }

        const showSnackbar = showSnackbarAction({
            id: DOWNLOAD_APP_SNAKCBAR_ID,
            className: showBottomControls ? styles.downloadSnackbar : undefined,
            type: SnackbarTypes.mobileAppDownload,
            text: 'Удобнее смотреть файлы в Облаке',
            iconSize: 'flex',
            closable: true,
            disableCloseTimeout: true,
            buttonText: 'Скачать приложение',
            onButtonClick: handleOnMobileAppClick,
        });

        dispatch(snackbarVisible ? hideSnackbar : showSnackbar);
        setSnackbarVisible(!snackbarVisible);
    };

    useEffect(() => {
        if (!showSnackbar) {
            return;
        }

        const hideSnackbar = () => dispatch(hideSnackbarAction(DOWNLOAD_APP_SNAKCBAR_ID));
        document.addEventListener('click', !abPublicMobileSnackbarToggle ? hideSnackbar : toggleSnackbar);
        return () => document.removeEventListener('click', !abPublicMobileSnackbarToggle ? hideSnackbar : toggleSnackbar);
    }, [snackbarVisible]);

    useEffect(() => {
        if (!file) {
            return;
        }

        if (showSnackbar) {
            toggleSnackbar();
        }
        removeViewerPlaceholder();
    }, [file]);

    useRouteChangeProcessing({
        storage,
        path: location.pathname.replace(`/${storage}/`, ''),
        search: location.search,
    });

    const onIntersecting = () => {
        setIsVisible(true);
    };
    const onIntersectingOut = () => {
        setIsVisible(false);
    };

    const refIntersection = useIntersectionObserver({
        onIntersecting,
        onIntersectingOut,
        rootMargin: '30px',
    });

    const handleOnClone = useCallback(() => {
        const eventAction = isAuthorized ? 'clone-click' : 'auth-click';

        sendGA(eventAction);

        sendXray([EVENT_CATEGORY, eventAction]);

        if (isAuthorized) {
            toolbarActions.clone({
                id: item.id,
                destination: '',
                source: 'public-files',
            });
        } else {
            dispatch(
                authPopup({
                    closable: true,
                    loginRequest: true,
                    successPage: `${window.location.href}${window.location.search}`,
                })
            );
        }
    }, [isAuthorized, item.id]);

    const sendGA = (action) => {
        sendXray(['publ-viewic-file', action, isAuthorized ? 'user' : 'anon']);
    };

    const { onDownload: onDownloadHook } = useCallbacks({
        storage,
        id: item.id,
        onClose: handleOnClone,
        sendAnalytics: sendGA,
        isAppViewer: params && Boolean(params.is_app_viewer),
        source: OptionsModalSource.PUBLIC_FILE,
    });

    const openToolbarOptions = useCallback(() => {
        renderMobileOptionsModal({
            id: item.id,
            storage,
            onlyActions: true,
            sendAnalytics: sendDwh,
            skipRename: true,
            skipDelete: true,
            source: OptionsModalSource.PUBLIC_FILE,
            onOpenClick: showOpenButton ? openEditorInNewTab : undefined,
        });
    }, [item.id, sendDwh, storage]);

    const handleOnDownload = () => {
        const eventAction = 'download-click';
        sendGA(eventAction);

        sendXray([EVENT_CATEGORY, eventAction]);

        onDownloadHook();
    };

    const content = useMemo(() => {
        if (isOverquota && overquotaUserLockInPublic?.enable) {
            return <Overquota overquotaSize={quota.value} isMobile />;
        }

        if (showDisabledForOverquotaOwner) {
            return (
                <div className={styles.wrapperQuota}>
                    <div className={styles.emptyOverquota}>
                        <EmptyPublicOverquota forceDark={!PUBLIC_SHOVE} small />
                    </div>
                </div>
            );
        }

        if (!file) {
            return null;
        }

        return (
            <MobileViewerItem
                isPublic
                selected
                forceArchiveViewer
                itemId={file?.id}
                isSingle={true}
                handleDownload={() => downloadItem({ itemOrId: file, fromViewer: true })}
                storage={storage}
                isViewContentOnly={isViewContentOnly}
                sendAnalytics={sendDwh}
                openToolbarOptions={openToolbarOptions}
                showToolbarInArchive={isViewContentOnly}
                showDoc={!showEditButton}
            />
        );
    }, [isOverquota, showDisabledForOverquotaOwner, file, storage, sendDwh, openToolbarOptions, showEditButton, quota.value]);

    if (!file) {
        return null;
    }

    // tempexp_17777-start
    const minHeight = `max(calc(var(--dvh, --vh) * 100 - ${
        TOOLBAR_HEIGHT +
        (!abPublicMobileHeaderValue || abPublicMobileHeaderValue === 'a' || isSuperAppWebView ? HEADER_HEIGHT : 0) +
        (HIDE_ADS ? 0 : DEFAULT_TOP_HEIGHT) +
        (showSnackbar ? 82 : 0)
    }px), 50vh)`;
    // tempexp_17777-end

    return (
        <div className={styles.root}>
            {!viewerOnly && <ViewerToolbarMobileNew file={item} openToolbarOptions={openToolbarOptions} />}

            <div className={styles.content} style={{ minHeight, maxHeight: minHeight }}>
                <div
                    className={clsx(styles.preview, {
                        [styles.previewDoc]: isDocumentItem,
                        [styles.previewDocMobileOnPremR7]: isDocumentItem && IS_MOBILE_BROWSER && isOnPremR7Product,
                    })}
                    ref={previewRef}
                >
                    {content}
                </div>
                {showBottomControls && (
                    <BottomControls
                        item={item}
                        onDownload={handleOnDownload}
                        onOpen={openEditorInNewTab}
                        showDownload={showDownloadButton}
                        showOpen={showOpenButton}
                    />
                )}
            </div>

            {withMailSpacePromo && <MailspacePublicTrialBanner />}

            {!HIDE_ADS && !viewerOnly && !isSuperAppWebView && (
                <div className={styles.adsWrapper} ref={refIntersection}>
                    {isVisible && <RollContentAds darkMode showElementIsInViewport />}
                </div>
            )}
            {/**
             * @expFlag public-app-download-banner
             * @ticket CLOUDWEB-20131
             * @start
             */}
            {publicAppDownloadBannerTouch &&
                publicAppDownloadBannerTouch.type === 'a' &&
                bannerRoot &&
                createPortal(<MobileAppBanner href={publicAppDownloadBannerTouch.href} />, bannerRoot)}
            {/**
             * @expFlag public-app-download-banner
             * @ticket CLOUDWEB-20131
             * @end
             */}
        </div>
    );
});

PublicFileConnected.displayName = 'PublicFileConnected';

type BottomControlsProps = {
    item: IFile;
    showOpen: boolean;
    showDownload: boolean;
    onOpen: () => void;
    onDownload: () => void;
};

function BottomControls({ item, onDownload, onOpen, showDownload, showOpen }: BottomControlsProps) {
    return (
        <div className={styles.controlsWrapper} data-qa-id="bottom-controls">
            {showOpen && (
                <Button
                    className={clsx(styles.btn)}
                    size="l"
                    stretched
                    appearance="accent-invariable"
                    mode="primary"
                    onClick={onOpen}
                    data-qa-id="open"
                >
                    Открыть
                </Button>
            )}

            {showDownload && (
                <Button
                    className={clsx(styles.btn, { [styles.secondary]: showOpen })}
                    size="l"
                    stretched
                    appearance={!showOpen ? 'accent-invariable' : 'neutral'}
                    mode={!showOpen ? 'primary' : 'tertiary'}
                    before={<DownloadIcon width={24} height={24} />}
                    onClick={onDownload}
                    data-qa-id="download"
                >
                    Скачать файл ({item.size})
                </Button>
            )}
        </div>
    );
}
